// Memory Management and Barba Integration Manager
// Centralized cleanup and initialization for all JS components

class MemoryManager {
  constructor() {
    this.instances = new Map();
    this.timers = new Set();
    this.eventListeners = new Map();
    this.scrollTriggers = new Set();
    this.gsapTimelines = new Set();
  }

  // Register an instance for cleanup
  register(name, instance) {
    this.instances.set(name, instance);
  }

  // Register a timer for cleanup
  registerTimer(timer) {
    this.timers.add(timer);
  }

  // Register an event listener for cleanup
  registerEventListener(element, event, handler, options) {
    const key = `${element.constructor.name}_${event}`;
    if (!this.eventListeners.has(key)) {
      this.eventListeners.set(key, []);
    }
    this.eventListeners.get(key).push({ element, event, handler, options });
    element.addEventListener(event, handler, options);
  }

  // Register a ScrollTrigger for cleanup
  registerScrollTrigger(trigger) {
    this.scrollTriggers.add(trigger);
  }

  // Register a GSAP timeline for cleanup
  registerTimeline(timeline) {
    this.gsapTimelines.add(timeline);
  }

  // Clean up all registered items
  cleanup() {
    console.log('MemoryManager: Starting cleanup');

    // Clear timers
    this.timers.forEach(timer => {
      clearTimeout(timer);
      clearInterval(timer);
    });
    this.timers.clear();

    // Remove event listeners
    this.eventListeners.forEach((listeners, key) => {
      listeners.forEach(({ element, event, handler, options }) => {
        if (element && element.removeEventListener) {
          element.removeEventListener(event, handler, options);
        }
      });
    });
    this.eventListeners.clear();

    // Kill ScrollTriggers
    this.scrollTriggers.forEach(trigger => {
      if (trigger && trigger.kill) {
        trigger.kill();
      }
    });
    this.scrollTriggers.clear();

    // Kill GSAP timelines
    this.gsapTimelines.forEach(timeline => {
      if (timeline && timeline.kill) {
        timeline.kill();
      }
    });
    this.gsapTimelines.clear();

    // Call cleanup on instances
    this.instances.forEach((instance, name) => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      } else if (instance && typeof instance.cleanup === 'function') {
        instance.cleanup();
      }
    });
    this.instances.clear();

    console.log('MemoryManager: Cleanup complete');
  }

  // Get a registered instance
  getInstance(name) {
    return this.instances.get(name);
  }
}

// Global memory manager instance
window.memoryManager = new MemoryManager();

// Enhanced component initialization system
class ComponentManager {
  constructor() {
    this.initFunctions = new Map();
  }

  // Register a component initialization function
  register(name, initFunction, cleanupFunction = null) {
    this.initFunctions.set(name, { init: initFunction, cleanup: cleanupFunction });
  }

  // Initialize all registered components
  initializeAll() {
    console.log('ComponentManager: Initializing all components');
    this.initFunctions.forEach((functions, name) => {
      try {
        const instance = functions.init();
        if (instance) {
          window.memoryManager.register(name, instance);
        }
        console.log(`ComponentManager: Initialized ${name}`);
      } catch (error) {
        console.error(`ComponentManager: Failed to initialize ${name}:`, error);
      }
    });
  }

  // Cleanup specific component
  cleanup(name) {
    const functions = this.initFunctions.get(name);
    const instance = window.memoryManager.getInstance(name);
    
    if (functions && functions.cleanup && instance) {
      try {
        functions.cleanup(instance);
        console.log(`ComponentManager: Cleaned up ${name}`);
      } catch (error) {
        console.error(`ComponentManager: Failed to cleanup ${name}:`, error);
      }
    }
  }
}

// Global component manager instance
window.componentManager = new ComponentManager();

// Barba integration with proper cleanup
if (typeof barba !== 'undefined') {
  // Before leaving page - clean up everything
  barba.hooks.beforeLeave(() => {
    console.log('Barba: Page leaving - starting cleanup');
    
    // Kill all ScrollTriggers
    if (window.ScrollTrigger) {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    }
    
    // Kill GSAP ScrollSmoother
    if (window.gsapSmoother) {
      window.gsapSmoother.kill();
      window.gsapSmoother = null;
    }
    
    // Run memory manager cleanup
    window.memoryManager.cleanup();
    
    // Reset scroll position
    window.scrollTo(0, 0);
  });

  // After entering new page - reinitialize
  barba.hooks.afterEnter(() => {
    console.log('Barba: Page entered - reinitializing components');
    
    // Small delay to ensure DOM is ready
    setTimeout(() => {
      // Reinitialize GSAP
      if (window.gsapUtils && window.gsapUtils.reinitialize) {
        window.gsapUtils.reinitialize();
      }
      
      // Reinitialize all components
      window.componentManager.initializeAll();
      
      // Refresh ScrollTrigger
      if (window.ScrollTrigger) {
        ScrollTrigger.refresh();
      }
    }, 100);
  });

  // Final cleanup and refresh
  barba.hooks.after(() => {
    // Trigger page loaded event for any remaining scripts
    window.dispatchEvent(new Event('page:loaded'));
    window.dispatchEvent(new Event('barba:after'));
  });
}

// Utility functions for components to use
window.registerComponent = function(name, initFunction, cleanupFunction) {
  window.componentManager.register(name, initFunction, cleanupFunction);
};

window.safeSetTimeout = function(callback, delay) {
  const timer = setTimeout(callback, delay);
  window.memoryManager.registerTimer(timer);
  return timer;
};

window.safeSetInterval = function(callback, delay) {
  const timer = setInterval(callback, delay);
  window.memoryManager.registerTimer(timer);
  return timer;
};

window.safeAddEventListener = function(element, event, handler, options) {
  window.memoryManager.registerEventListener(element, event, handler, options);
};

console.log('Memory Manager and Component System initialized');
